import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Crown, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";

const COLORS = ["var(--accent)", "var(--secondary)", "var(--primary)", "var(--pink)", "var(--green)"];

function loadPayPalSdk(clientId) {
  return new Promise((resolve, reject) => {
    if (window.paypal) return resolve(window.paypal);
    const existing = document.querySelector("script[data-pp]");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.paypal));
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&currency=USD&intent=capture`;
    s.dataset.pp = "1";
    s.onload = () => resolve(window.paypal);
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

export default function Membership() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [pkgs, setPkgs] = useState([]);
  const [config, setConfig] = useState(null);
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => {
    api.get("/memberships").then((r) => setPkgs(r.data));
    api.get("/payments/config").then((r) => setConfig(r.data)).catch(() => setConfig({ stripe_enabled: false, paypal_client_id: "" }));
  }, []);

  const buyStripe = async (id) => {
    if (!user) { toast.error("Please log in first"); return nav("/login"); }
    setLoadingId(id);
    try {
      const { data } = await api.post("/payments/checkout", { package_id: id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
      setLoadingId(null);
    }
  };

  return (
    <div className="pastel-grain min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-center mb-10">
          <div className="sticker inline-flex" style={{ background: "var(--pink)" }}>
            <Sparkles size={14} strokeWidth={2.5} /> Become a member
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-3">
            Choose your plan
          </h1>
          <p className="mt-3 text-lg text-zinc-700 font-medium max-w-xl mx-auto">
            Support the community, remove ads and unlock exclusive features.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="membership-list">
          {pkgs.map((p, i) => (
            <PackageCard key={p.id} p={p} i={i} user={user}
              config={config} loading={loadingId === p.id}
              onStripe={() => buyStripe(p.id)}
              onPaypalSuccess={async () => {
                const fresh = await api.get("/auth/me").then((r) => r.data).catch(() => null);
                if (fresh) setUser(fresh);
                nav(`/payment-success?session_id=paypal-${p.id}`);
              }} />
          ))}
        </div>
      </div>
    </div>
  );
}

function PackageCard({ p, i, user, config, loading, onStripe, onPaypalSuccess }) {
  const ppRef = useRef(null);
  const ppLoaded = useRef(false);
  const ppEnabled = config?.paypal_client_id;
  const stripeEnabled = config?.stripe_enabled;
  const nav = useNavigate();

  useEffect(() => {
    if (!ppEnabled || !ppRef.current || ppLoaded.current) return;
    ppLoaded.current = true;
    loadPayPalSdk(config.paypal_client_id).then((paypal) => {
      if (!ppRef.current) return;
      ppRef.current.innerHTML = "";
      paypal.Buttons({
        style: { layout: "horizontal", height: 40, label: "pay", tagline: false, shape: "rect" },
        createOrder: async () => {
          if (!user) { toast.error("Please log in first"); nav("/login"); throw new Error("auth"); }
          try {
            const { data } = await api.post("/payments/paypal/create", {
              package_id: p.id, origin_url: window.location.origin,
            });
            return data.order_id;
          } catch (e) { toast.error(formatError(e.response?.data?.detail) || "PayPal init failed"); throw e; }
        },
        onApprove: async (data) => {
          try {
            const r = await api.post(`/payments/paypal/capture/${data.orderID}`);
            if (r.data.payment_status === "paid") {
              toast.success("Payment complete!");
              onPaypalSuccess?.();
            } else {
              toast.error("Payment not completed");
            }
          } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Capture failed"); }
        },
        onError: (err) => { console.error(err); toast.error("PayPal error"); },
      }).render(ppRef.current).catch(() => {});
    }).catch(() => { toast.error("Could not load PayPal"); });
  }, [ppEnabled, config?.paypal_client_id, p.id, user, nav, onPaypalSuccess]);

  return (
    <div data-testid={`membership-card-${p.id}`}
      className="brutal-border brutal-shadow rounded-2xl p-7 fade-up"
      style={{ background: COLORS[i % COLORS.length], animationDelay: `${i * 60}ms` }}>
      <div className="flex items-center gap-2 mb-2">
        <Crown size={20} strokeWidth={2.5} />
        <h3 className="font-display font-black text-2xl">{p.name}</h3>
      </div>
      <p className="text-sm text-zinc-700 font-medium">{p.description}</p>
      <div className="mt-5">
        <span className="font-display text-5xl font-black">${p.price}</span>
        <span className="text-zinc-700 font-medium"> / {p.duration_days} days</span>
      </div>
      <ul className="mt-5 space-y-2">
        {p.features.map((f, idx) => (
          <li key={idx} className="flex items-start gap-2 text-sm font-medium">
            <Check size={16} strokeWidth={2.5} className="mt-0.5 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-6 space-y-3">
        {stripeEnabled && (
          <Button onClick={onStripe} disabled={loading} data-testid={`buy-stripe-${p.id}`}
            className="w-full h-11 brutal-border brutal-shadow rounded-xl font-bold"
            style={{ background: "var(--ink)", color: "white" }}>
            {loading ? "Redirecting to Stripe..." : "Pay with card (Stripe)"}
          </Button>
        )}
        {ppEnabled ? (
          <div ref={ppRef} data-testid={`paypal-${p.id}`} className="brutal-border rounded-xl bg-white p-1 min-h-[44px]" />
        ) : null}
        {!stripeEnabled && !ppEnabled && (
          <div className="text-xs text-zinc-700 font-bold brutal-border rounded-lg p-2 bg-white text-center">
            No payment method configured. Admin must set up Stripe or PayPal in /admin → Payments.
          </div>
        )}
      </div>
    </div>
  );
}
